'use client';

import { useState } from 'react';
import { MapPin, Phone, Mail, Clock, CircleCheck } from 'lucide-react';
import siteTheme from '@/lib/theme';
import type { SectionContactForm, SectionContactInfo } from '@/lib/cms';
import { submitContactForm } from '@/lib/api';
import { parseMarkdown } from '@/lib/markdown';

const { colors, fonts } = siteTheme;

const INQUIRY_TYPES = [
  'Reservations',
  'Events & Weddings',
  'Dining',
  'Spa & Wellness',
  'General Enquiry',
] as const;

type InquiryType = (typeof INQUIRY_TYPES)[number];

const fieldWrap: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.8rem',
};

const labelStyle: React.CSSProperties = {
  fontFamily: fonts.sans,
  fontSize: '1.1rem',
  fontWeight: 500,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: colors.goldLight,
};

const FIELD_CSS = `
  .cf-field {
    font-family: var(--font-sans), sans-serif;
    font-size: 1.5rem;
    font-weight: 300;
    color: #786947;
    background-color: #fff !important;
    border: 1px solid rgba(120, 105, 71, 0.5) !important;
    border-radius: 2px;
    padding: 1.1rem 1.4rem !important;
    outline: none !important;
    width: 100%;
    box-sizing: border-box;
    transition: border-color 0.2s;
    -webkit-appearance: none;
    appearance: none;
    box-shadow: none !important;
  }
  .cf-field::placeholder {
    color: rgba(120, 105, 71, 0.4);
  }
  .cf-field:focus {
    border-color: #786947 !important;
  }
  .cf-select {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23786947' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 1.4rem center;
    background-color: #fff !important;
    padding-right: 3.6rem !important;
    cursor: pointer;
  }
  .cf-textarea {
    resize: vertical;
    min-height: 14rem;
  }
  .cf-field::-webkit-calendar-picker-indicator {
    opacity: 0.5;
    cursor: pointer;
  }
`;


/* ── Info card (right column) ───────────────────────────────── */
function InfoCard({ data }: { data: SectionContactInfo }) {
  const groupLabel: React.CSSProperties = {
    fontFamily: fonts.sans,
    fontSize: '1.05rem',
    fontWeight: 600,
    letterSpacing: '0.22em',
    textTransform: 'uppercase',
    color: colors.gold,
    marginBottom: '1rem',
  };
  const line: React.CSSProperties = {
    fontFamily: fonts.sans,
    fontSize: '1.4rem',
    fontWeight: 300,
    lineHeight: 1.7,
    color: 'rgba(255,255,255,0.82)',
  };
  const divider: React.CSSProperties = {
    height: '1px',
    backgroundColor: 'rgba(255,255,255,0.1)',
    margin: '2.4rem 0',
  };

  return (
    <div style={{
      backgroundColor: colors.navyDeep,
      padding: 'clamp(3.2rem, 4vw, 5rem)',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: 0,
    }}>
      <p style={{
        fontFamily: fonts.script,
        fontSize: 'clamp(2.2rem, 2.5vw, 3rem)',
        color: colors.gold,
        marginBottom: '0.4rem',
      }}>
        Find Us
      </p>
      <h3 style={{
        fontFamily: fonts.serif,
        fontWeight: 300,
        fontSize: 'clamp(2rem, 2.2vw, 2.8rem)',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: '#fff',
        marginBottom: 'clamp(2.8rem, 3.5vw, 4rem)',
      }}>
        Contact & Location
      </h3>

      {/* Address */}
      {data.address && (
        <>
          <p style={groupLabel}>Location</p>
          <div style={{ display: 'flex', gap: '1.2rem', alignItems: 'flex-start', marginBottom: 0 }}>
            <span style={{ color: colors.gold }}><MapPin size={18} strokeWidth={1.5} style={{ flexShrink: 0, marginTop: '0.2rem' }} /></span>
            <div style={line} dangerouslySetInnerHTML={{ __html: parseMarkdown(data.address) }} />
          </div>
          <div style={divider} />
        </>
      )}

      {/* Phones */}
      {data.phones?.length > 0 && (
        <>
          <p style={groupLabel}>Contact Numbers</p>
          <div style={{ display: 'flex', gap: '1.2rem', alignItems: 'flex-start' }}>
            <span style={{ color: colors.gold }}><Phone size={18} strokeWidth={1.5} style={{ flexShrink: 0, marginTop: '0.2rem' }} /></span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {data.phones.map(ph => (
                <p key={ph.id} style={line}>
                  {ph.label && <span style={{ color: 'rgba(255,255,255,0.45)', marginRight: '0.4rem' }}>{ph.label}:</span>}
                  <a href={`tel:${ph.number.replace(/\\s+/g, '')}`} style={{ color: 'inherit', textDecoration: 'none' }}>{ph.number}</a>
                </p>
              ))}
            </div>
          </div>
          <div style={divider} />
        </>
      )}

      {/* Emails */}
      {data.emails?.length > 0 && (
        <>
          <p style={groupLabel}>Email Addresses</p>
          <div style={{ display: 'flex', gap: '1.2rem', alignItems: 'flex-start' }}>
            <span style={{ color: colors.gold }}><Mail size={18} strokeWidth={1.5} style={{ flexShrink: 0, marginTop: '0.2rem' }} /></span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {data.emails.map(em => (
                <p key={em.id} style={line}>
                  {em.label && <span style={{ color: 'rgba(255,255,255,0.45)', marginRight: '0.4rem' }}>{em.label}:</span>}
                  <a href={`mailto:${em.email}`} style={{ color: 'inherit', textDecoration: 'none' }}>{em.email}</a>
                </p>
              ))}
            </div>
          </div>
          {data.hours && <div style={divider} />}
        </>
      )}

      {/* Hours */}
      {data.hours && (
        <>
          <p style={groupLabel}>Hours</p>
          <div style={{ display: 'flex', gap: '1.2rem', alignItems: 'flex-start' }}>
            <span style={{ color: colors.gold }}><Clock size={18} strokeWidth={1.5} style={{ flexShrink: 0, marginTop: '0.2rem' }} /></span>
            <p style={line}>{data.hours}</p>
          </div>
        </>
      )}
    </div>
  );
}

/* ── Main component ─────────────────────────────────────────── */
export default function ContactFormSection({
  data,
  contactInfo,
}: {
  data: SectionContactForm;
  contactInfo?: SectionContactInfo | null;
}) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    inquiry_type: 'General Enquiry' as InquiryType,
    checkin_date: '',
    checkout_date: '',
    guests: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const showDateFields = form.inquiry_type === 'Reservations';

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError('Please fill in Name, Email and Message.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await submitContactForm({ ...form, to_email: data.contact_email });
      setSuccess(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  const pad = 'clamp(2rem, 6vw, 8rem)';

  /* ── Success state ─────────────────────────────────────────── */
  if (success) {
    return (
      <section style={{ backgroundColor: colors.bg, padding: `clamp(7rem, 11vw, 13rem) ${pad}` }}>
        <div style={{ maxWidth: '72rem', margin: '0 auto', textAlign: 'center' }}>
          <CircleCheck size={56} strokeWidth={1} color={colors.gold} style={{ margin: '0 auto 2.4rem', display: 'block' }} />
          <p style={{ fontFamily: fonts.script, fontSize: 'clamp(2.4rem, 3vw, 3.8rem)', color: colors.gold, margin: '0 0 1.2rem' }}>
            Message Received
          </p>
          <h2 style={{ fontFamily: fonts.serif, fontWeight: 300, fontSize: 'clamp(2.4rem, 4vw, 4.8rem)', color: colors.navyDeep, letterSpacing: '0.05em', textTransform: 'uppercase', margin: '0 0 2.4rem' }}>
            Thank You
          </h2>
          <p style={{ fontFamily: fonts.sans, fontSize: 'clamp(1.4rem, 1.3vw, 1.6rem)', color: colors.goldLight, lineHeight: 1.8, maxWidth: '52rem', margin: '0 auto' }}>
            {data.success_message || 'Thank you for reaching out. We will get back to you within 24 hours.'}
          </p>
        </div>
      </section>
    );
  }

  /* ── Form ─────────────────────────────────────────────────── */
  const formEl = (
    <div>
      <style dangerouslySetInnerHTML={{ __html: FIELD_CSS }} />

      {/* Column heading */}
      {data.subtitle && (
        <p style={{ fontFamily: fonts.script, fontSize: 'clamp(2.2rem, 2.5vw, 3rem)', color: colors.gold, margin: '0 0 0.6rem' }}>
          {data.subtitle}
        </p>
      )}
      <h2 style={{
        fontFamily: fonts.serif,
        fontWeight: 300,
        fontSize: 'clamp(2.4rem, 3.5vw, 5rem)',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: colors.navyDeep,
        margin: '0 0 clamp(3rem, 4vw, 5rem)',
        lineHeight: 1.1,
      }}>
        {data.heading || 'Get in Touch'}
      </h2>

      {data.description && (
        <div className="section-body cms-rich-text" style={{ fontSize: 'clamp(1.4rem, 1.3vw, 1.6rem)', lineHeight: 1.8, color: colors.goldLight, marginBottom: 'clamp(2.4rem, 3vw, 3.6rem)' }} dangerouslySetInnerHTML={{ __html: parseMarkdown(data.description) }} />
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div style={{ display: 'grid', gap: 'clamp(2rem, 2.5vw, 3rem)' }}>

          {/* Name + Email */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(20rem, 1fr))', gap: 'clamp(1.6rem, 2vw, 2.8rem)' }}>
            <div style={fieldWrap}>
              <label htmlFor="cf-name" style={labelStyle}>Full Name <span style={{ color: colors.gold }}>*</span></label>
              <input
                id="cf-name" className="cf-field"
                type="text" name="name" value={form.name}
                onChange={handleChange}
                placeholder="Your full name"
                autoComplete="name"
                required
              />
            </div>
            <div style={fieldWrap}>
              <label htmlFor="cf-email" style={labelStyle}>Email Address <span style={{ color: colors.gold }}>*</span></label>
              <input
                id="cf-email" className="cf-field"
                type="email" name="email" value={form.email}
                onChange={handleChange}
                placeholder="your@email.com"
                autoComplete="email"
                required
              />
            </div>
          </div>

          {/* Phone + Inquiry Type */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(20rem, 1fr))', gap: 'clamp(1.6rem, 2vw, 2.8rem)' }}>
            <div style={fieldWrap}>
              <label htmlFor="cf-phone" style={labelStyle}>Phone Number</label>
              <input
                id="cf-phone" className="cf-field"
                type="tel" name="phone" value={form.phone}
                onChange={handleChange}
                placeholder="+91 00000 00000"
                autoComplete="tel"
              />
            </div>
            <div style={fieldWrap}>
              <label htmlFor="cf-inquiry" style={labelStyle}>Nature of Enquiry</label>
              <select
                id="cf-inquiry" className="cf-field cf-select"
                name="inquiry_type" value={form.inquiry_type}
                onChange={handleChange}
              >
                {INQUIRY_TYPES.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Dates + Guests — only for Reservations */}
          {showDateFields && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(14rem, 1fr))', gap: 'clamp(1.6rem, 2vw, 2.8rem)' }}>
              <div style={fieldWrap}>
                <label htmlFor="cf-checkin" style={labelStyle}>Check-in Date</label>
                <input
                  id="cf-checkin" className="cf-field"
                  type="date" name="checkin_date" value={form.checkin_date}
                  onChange={handleChange}
                />
              </div>
              <div style={fieldWrap}>
                <label htmlFor="cf-checkout" style={labelStyle}>Check-out Date</label>
                <input
                  id="cf-checkout" className="cf-field"
                  type="date" name="checkout_date" value={form.checkout_date}
                  onChange={handleChange}
                />
              </div>
              <div style={fieldWrap}>
                <label htmlFor="cf-guests" style={labelStyle}>No. of Guests</label>
                <input
                  id="cf-guests" className="cf-field"
                  type="number" name="guests" value={form.guests}
                  onChange={handleChange}
                  placeholder="2" min={1} max={20}
                />
              </div>
            </div>
          )}

          {/* Message */}
          <div style={fieldWrap}>
            <label htmlFor="cf-message" style={labelStyle}>Your Message <span style={{ color: colors.gold }}>*</span></label>
            <textarea
              id="cf-message" className="cf-field cf-textarea"
              name="message" value={form.message}
              onChange={handleChange}
              placeholder="Tell us how we can assist you..."
              rows={5}
              required
            />
          </div>

          {/* Error */}
          {error && (
            <p style={{ fontFamily: fonts.sans, fontSize: '1.3rem', color: '#c0392b', margin: 0 }}>
              {error}
            </p>
          )}

          {/* Submit */}
          <div>
            <button
              type="submit"
              disabled={loading}
              className="btn-luxury-nav"
              style={{
                fontFamily: fonts.sans,
                fontSize: '1.2rem',
                fontWeight: 500,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: loading ? colors.goldLight : colors.white,
                backgroundColor: loading ? 'rgba(120,105,71,0.4)' : colors.navyDeep,
                border: `1px solid ${colors.navyDeep}`,
                padding: '1.6rem 4rem',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Sending...' : 'Send Enquiry'}
            </button>
          </div>

        </div>
      </form>
    </div>
  );

  /* ── Two-column layout (with contact info) ─────────────────── */
  if (contactInfo) {
    return (
      <>
        <section style={{ backgroundColor: colors.bg, padding: `clamp(7rem, 11vw, 13rem) ${pad}` }}>
          <div style={{ maxWidth: '144rem', margin: '0 auto' }}>
            <div
              className="grid grid-cols-1 lg:grid-cols-[1fr_42rem] xl:grid-cols-[1fr_48rem]"
              style={{ gap: 'clamp(4rem, 6vw, 8rem)', alignItems: 'stretch' }}
            >
              {/* Left: form */}
              <div>{formEl}</div>

              {/* Right: info card */}
              <div>
                <InfoCard data={contactInfo} />
              </div>
            </div>
          </div>
        </section>

      </>
    );
  }

  /* ── Fallback: centered single-column (no contact info) ─────── */
  return (
    <section style={{ backgroundColor: colors.bg, padding: `clamp(7rem, 11vw, 13rem) ${pad}` }}>
      <div style={{ maxWidth: '86rem', margin: '0 auto' }}>
        {formEl}
      </div>
    </section>
  );
}
